/**
 * SourcePreviewModal – Lightweight drawer/modal that shows
 * a snippet or metadata for a single source.
 *
 * For internal sources: shows title, fiscal year, section, page ref, chunk text.
 * For web sources: shows title, URL, publisher, snippet text.
 */
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    IconButton,
    Link,
} from '@mui/material';
import {
    Close,
    Description,
    Language,
    OpenInNew,
    ContentCopy,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const SourcePreviewModal = ({ open, onClose, source }) => {
    if (!source) return null;

    const isWeb = source.sourceType === 'web';

    const copyText = () => {
        const text = [
            `[${source.refId}] ${source.title}`,
            isWeb && source.url ? source.url : '',
            source.pageRef ? `Page: ${source.pageRef}` : '',
            source.fiscalYear ? `FY ${source.fiscalYear}` : '',
            source.sectionName || '',
        ].filter(Boolean).join('\n');
        navigator.clipboard.writeText(text);
        toast.success('Citation copied', { autoClose: 1200 });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: '70vh',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pb: 1,
                    pr: 6,
                }}
            >
                {isWeb ? (
                    <Language sx={{ color: 'var(--warning)', fontSize: 20 }} />
                ) : (
                    <Description sx={{ color: 'var(--brand-2)', fontSize: 20 }} />
                )}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                    [{source.refId}] {source.title}
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: 'absolute', right: 12, top: 12 }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* ── Meta chips ── */}
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                        size="small"
                        label={isWeb ? 'Web source' : 'Internal document'}
                        sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            backgroundColor: isWeb ? 'var(--bg-3)' : 'var(--bg-3)',
                            color: isWeb ? 'var(--warning)' : 'var(--brand-2)',
                        }}
                    />
                    {source.fiscalYear && (
                        <Chip size="small" label={`FY ${source.fiscalYear}`} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    )}
                    {source.sectionName && (
                        <Chip size="small" label={source.sectionName} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    )}
                    {source.pageRef && (
                        <Chip size="small" label={source.pageRef} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    )}
                    {source.publisher && (
                        <Chip size="small" label={source.publisher} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    )}
                    {source.score > 0 && (
                        <Chip
                            size="small"
                            label={`Score: ${(source.score * 100).toFixed(0)}%`}
                            variant="outlined"
                            color={source.score >= 0.76 ? 'success' : source.score >= 0.5 ? 'warning' : 'default'}
                            sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                    )}
                </Box>

                {/* ── URL ── */}
                {isWeb && source.url && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'var(--text-2)', display: 'block', mb: 0.3 }}>
                            Source URL
                        </Typography>
                        <Link
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: '0.82rem', wordBreak: 'break-all' }}
                        >
                            {source.url}
                            <OpenInNew sx={{ fontSize: 12, ml: 0.5, verticalAlign: 'middle' }} />
                        </Link>
                    </Box>
                )}

                {/* ── Doc ID (internal) ── */}
                {!isWeb && source.docId && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ color: 'var(--text-2)', display: 'block', mb: 0.3 }}>
                            Document ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'var(--text-2)', fontFamily: 'monospace' }}>
                            {source.docId}
                        </Typography>
                    </Box>
                )}

                {/* ── Passage snippet ── */}
                <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'var(--text-2)', display: 'block', mb: 0.5 }}>
                        {source.snippet ? 'Source passage' : 'Source details'}
                    </Typography>
                    <Box
                        sx={{
                            p: 1.5,
                            backgroundColor: 'var(--bg-3)',
                            borderRadius: 2,
                            border: '1px solid var(--border)',
                        }}
                    >
                        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'var(--text-2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {source.snippet || [
                                source.title,
                                source.sectionName && ` — ${source.sectionName}`,
                                source.fiscalYear && ` (FY${source.fiscalYear})`,
                                source.pageRef && ` ${source.pageRef}`,
                            ].filter(Boolean).join('')}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1 }}>
                <Button
                    size="small"
                    startIcon={<ContentCopy sx={{ fontSize: 14 }} />}
                    onClick={copyText}
                    sx={{ textTransform: 'none', fontSize: '0.78rem' }}
                >
                    Copy citation
                </Button>
                {isWeb && source.url && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none', fontSize: '0.78rem' }}
                    >
                        Open in browser
                    </Button>
                )}
                <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: '0.78rem' }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SourcePreviewModal;
